import { useScrollReveal } from "@/hooks/useScrollReveal";

const reasons = [
  {
    icon: "ri-flask-line",
    title: "Chemically Identical",
    stat: "100%",
    statLabel: "Same Composition",
    text: "Lab-grown diamonds share the exact same physical, chemical, and optical properties as mined diamonds. The only difference is origin — one formed in the earth over billions of years, the other grown in a controlled environment over weeks. Both are real diamonds, certified and graded by the same independent gemological institutes.",
  },
  {
    icon: "ri-wallet-3-line",
    title: "Better Value",
    stat: "30-50%",
    statLabel: "Cost Savings",
    text: "Because the supply chain is shorter and more efficient, lab diamonds typically cost 30-50% less than mined diamonds of comparable quality. This means you can invest in a larger stone, a higher clarity grade, or a more elaborate setting — without compromising on quality or beauty.",
  },
  {
    icon: "ri-earth-line",
    title: "Lower Environmental Impact",
    stat: "7x",
    statLabel: "Less Land Disturbed",
    text: "Traditional diamond mining displaces massive amounts of earth, consumes significant energy, and can disrupt local ecosystems and communities. Lab-grown diamonds require substantially less land use and energy, with a considerably smaller carbon footprint per carat.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Fully Traceable Origin",
    stat: "IGI",
    statLabel: "Certified & Graded",
    text: "Every lab diamond from Zayan Atelier is independently certified by the International Gemological Institute (IGI). Your certificate documents the exact cut, color, clarity, and carat weight. You know precisely where your diamond came from and exactly what you are getting.",
  },
];

export default function WhyLabDiamonds() {
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section className="bg-zayan-bg border-t border-zayan-border py-16 md:py-20 lg:py-24 reveal" ref={sectionRef}>
      <div className="px-6 lg:px-16 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <p className="text-zayan-gold text-xs tracking-[0.2em] uppercase mb-3 font-sans">
            The Modern Choice
          </p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-zayan-gold leading-tight mb-4">
            Why Choose Lab Diamonds?
          </h2>
          <p className="text-zayan-textMuted text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
            Lab-grown diamonds are not imitations or substitutes. They are real diamonds — created with precision, certified with rigor, and offered with transparency.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {reasons.map((r, i) => (
            <div
              key={i}
              className="border border-zayan-border p-6 md:p-8 group hover:border-zayan-gold/30 transition-colors"
            >
              {/* Icon + Stat Row */}
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
                  <i className={`${r.icon} text-zayan-gold text-lg`} />
                </div>
                <div className="text-right">
                  <p className="font-serif text-2xl md:text-3xl text-zayan-gold leading-none">
                    {r.stat}
                  </p>
                  <p className="text-zayan-textDarker text-[10px] tracking-wider uppercase mt-1">
                    {r.statLabel}
                  </p>
                </div>
              </div>

              {/* Title */}
              <h3 className="text-zayan-text text-sm md:text-base font-medium tracking-wide mb-3">
                {r.title}
              </h3>

              {/* Description */}
              <p className="text-zayan-textMuted text-sm leading-relaxed">
                {r.text}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4 text-center sm:text-left">
          <div className="w-12 h-12 flex items-center justify-center rounded-full border border-zayan-gold/40 shrink-0">
            <i className="ri-vip-diamond-line text-zayan-gold text-lg" />
          </div>
          <p className="text-zayan-textMuted text-sm leading-relaxed max-w-xl">
            <span className="text-zayan-gold">Zayan Atelier</span> sources exclusively IGI-certified lab-grown diamonds. Every stone is graded using the same standards applied to mined diamonds — cut, color, clarity, and carat — so you can purchase with complete confidence.
          </p>
        </div>
      </div>
    </section>
  );
}